import { useCallback, useRef, useMemo, useEffect } from 'react';

import { IFilterBeneficiary } from '@/features/beneficiaries/store/models/beneficiary.model';
import {
  IAllBeneficiariesFiltersFormFields,
  parseAllBeneficiariesFiltersValue,
  useAllBeneficiariesFilter,
  TAllBeneficiariesFields,
} from '@/features/beneficiaries/hooks/AllBeneficiaries/useAllBeneficiariesFilter';
import { BENEF_ITEM_PER_PAGE } from '@/features/beneficiaries/constants';
import {
  TBeneficiaryData,
  TBeneficiaryAction,
} from '@/features/beneficiaries/hooks/AllBeneficiaries/useUserBeneficiariesData';
import { AllBeneficiariesHeaders } from '@/features/beneficiaries/components/AllBeneficiaries/AllBeneficiariesHeaders';
import { AllBeneficiariesRows } from '@/features/beneficiaries/components/AllBeneficiaries/AllBeneficiariesRows';
import { parseAllParameters, TUseTableDataReturn } from '@/ui-kit/table-filters/filters.type';
import {
  allBeneficiaryHeadersList,
  TAllBeneficiaryRowType,
} from '@/features/beneficiaries/store/models/allBeneficiaries.model';

export interface IUseAllBeneficiariesTableDataProps {
  data: TBeneficiaryData;
  getData: (filter: IFilterBeneficiary, hasInitalProps?: boolean) => void;
  actions: TBeneficiaryAction[];
  defaultFilters?: Partial<IAllBeneficiariesFiltersFormFields>;
  className?: string;
}

export const useAllBeneficiariesTableData = ({
  data,
  getData,
  actions,
}: IUseAllBeneficiariesTableDataProps): TUseTableDataReturn<TAllBeneficiaryRowType, TAllBeneficiariesFields> => {
  const { beneficiaries, meta: metaBeneficiaries, isLoading, filter: filterProps } = data;

  const queryParameters = useRef({
    pageNumber: undefined,
    filters: parseAllParameters<IAllBeneficiariesFiltersFormFields, IFilterBeneficiary>(
      filterProps,
      parseAllBeneficiariesFiltersValue,
    ),
  });

  const filtersDeps = JSON.stringify(filterProps);

  const updateData = useCallback(
    (initialFilters: Partial<IFilterBeneficiary> = {}, hasInitialProps: boolean = false) => {
      const { filters, ...parameters } = queryParameters.current;
      getData(
        {
          withMeta: 1,
          pageCount: BENEF_ITEM_PER_PAGE,
          ...parameters,
          ...filters,
          ...initialFilters,
        },
        hasInitialProps,
      );
    },
    [getData],
  );

  const onPageChange = useCallback(
    (pageNumber: number) => {
      queryParameters.current = {
        ...queryParameters.current,
        pageNumber,
      };
      updateData();
    },
    [updateData],
  );

  const handleFilterChange = useCallback(
    (filtersParameters) => {
      queryParameters.current = {
        ...queryParameters.current,
        filters: filtersParameters,
        pageNumber: 1,
      };
      updateData();
    },
    [updateData],
  );

  const { type, search, clearFilters, hasFilters } = useAllBeneficiariesFilter(handleFilterChange, filterProps);

  useEffect(() => {
    let queryProps = {};
    if (filtersDeps === '{}') {
      queryProps = !!type.value ? { usableForSct: type.value === 'scte' ? true : false } : {};
      queryParameters.current = {
        pageNumber: 1,
        filters: { ...queryProps },
      };
    } else {
      const filters = JSON.parse(filtersDeps);
      queryParameters.current = {
        pageNumber: 1,
        filters: parseAllParameters<IAllBeneficiariesFiltersFormFields, IFilterBeneficiary>(
          filters,
          parseAllBeneficiariesFiltersValue,
        ),
      };
    }

    updateData(
      {
        ...queryProps,
        ...queryParameters.current.pageNumber,
        ...queryParameters.current.filters,
      },
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersDeps]);

  const allHeaders = useMemo(() => {
    const fields = [];
    if (actions.includes('view')) {
      fields.push('userId');
    }
    if (type.value !== 'sddr') {
      fields.push('sepaCreditorIdentifier');
    }

    return allBeneficiaryHeadersList.filter((header) => !fields.includes(header));
  }, [actions, type.value]);

  const headers = AllBeneficiariesHeaders({
    headerList: allHeaders,
  });
  const rows = AllBeneficiariesRows({ beneficiaries, isLoading, actions });

  return {
    data: beneficiaries,
    fields: {
      type,
      search,
    },
    hasFilters,
    rows,
    headers,
    isLoading,
    totalRows: metaBeneficiaries?.totalResults,
    currentPage: metaBeneficiaries?.pageNumber ?? undefined,
    onPageChange,
    clearFilters,
  };
};
